/**
 * Five pools, and the absence between them.
 *
 * The hard part of this figure is drawing *nothing*. The invariant is that no
 * entity owns another — there is no `project.account_id`, no model pinned to a
 * project — and the natural way to illustrate five related things is to connect
 * them, which would state the opposite.
 *
 * So the pools sit unconnected, and what moves between them is a **route**: a
 * line that appears for one run, is labelled with that run's id, and is gone
 * before the second run draws its own. Two runs of the same project land on two
 * different accounts, which is the claim in one picture.
 */
import { useT } from '../../i18n/context'
import { useSequence } from '../../kit'
import { POOL_KEYS } from '../../data/fixtures'
import './PoolLattice.css'

/** Column centres, in the figure's own 900×250 coordinate space. */
const COLS = [90, 250, 450, 650, 810]
const POOL_Y = 56

/** The two runs, each naming which pool member it picked. Same project. */
const RUNS = [
  { id: '01JQ8F3K2M', account: 'claude-ops-1', host: 'srv-ist-1', y: 148, at: 0 },
  { id: '01JQ8G0T7Q', account: 'codex-ops-1', host: 'srv-fra-1', y: 210, at: 1 },
] as const

/** Hold times, one per step: pools alone · first route · both routes. */
const STEPS = [900, 1100, 0] as const

export function PoolLattice() {
  const t = useT()
  /*
   * Three steps, and they accumulate: pools alone, then one route, then both.
   *
   * The first draft looped and erased each route before drawing the next, on the
   * theory that a route is temporary and the figure should say so. It read as
   * broken — for most of any given second two thirds of the figure was empty,
   * and a reader arriving mid-cycle saw a diagram with nothing in it. The claim
   * is not "a route is brief", it is "the same project resolves to different
   * accounts on different runs", and that claim needs both routes on screen at
   * once. So the sequence runs once and stops with everything shown, which is
   * also exactly what reduced motion renders on the first frame.
   */
  const [ref, step] = useSequence(STEPS)

  return (
    <div ref={ref} className="kl-lattice">
      <svg viewBox="0 0 900 250" className="kl-lattice__svg" preserveAspectRatio="xMidYMid meet">
        <g className="kl-lattice__pools">
          {POOL_KEYS.map((key, i) => (
            <g key={key} transform={`translate(${COLS[i]}, ${POOL_Y})`}>
              <rect x={-64} y={-20} width={128} height={40} rx={4} className="kl-lattice__pool" />
              <text y={5} className="kl-lattice__poolLabel k-mono">
                {key}
              </text>
            </g>
          ))}
        </g>

        {/*
          The gap between the pools, named. Without this the reader supplies the
          missing edges from habit — every other architecture diagram they have
          seen has them — and reads the figure as "connections not drawn yet".
        */}
        <g className="kl-lattice__void" aria-hidden="true">
          {COLS.slice(0, -1).map((x, i) => (
            <g key={x} transform={`translate(${(x + COLS[i + 1]) / 2}, ${POOL_Y})`}>
              <text className="kl-lattice__voidLabel k-mono">{t('pools.fig.forbidden')}</text>
            </g>
          ))}
        </g>

        {RUNS.map((run, i) => {
          const visible = step > run.at
          // Down out of the account column, across, and up into the host column:
          // the two pools this run actually bound together, and nothing else.
          const d = `M ${COLS[0]} ${POOL_Y + 20} V ${run.y} H ${COLS[2]} V ${POOL_Y + 20}`
          return (
            <g key={run.id} className={`kl-lattice__run ${visible ? 'is-on' : ''}`}>
              <path d={d} className="kl-lattice__route" vectorEffect="non-scaling-stroke" />
              <text x={COLS[1]} y={run.y - 10} className="kl-lattice__runId k-mono">
                {t(i === 0 ? 'pools.fig.run1' : 'pools.fig.run2')} · {run.id} · {run.account} @{' '}
                {run.host}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
